import { useEffect } from 'react';
import { useAppStore, getRemoteConfigData } from '@src/shared/zustand';

interface UseRemoteConfigReturn<T> {
  value: T;
  loading: boolean;
}

/**
 * Hook genérico para carregar e acessar valores do Firebase Remote Config
 * Segue o mesmo padrão de implementação do FAQ
 *
 * @template T - Tipo do valor esperado (boolean, string, number, etc)
 * @param {string} configKey - Nome da chave no Remote Config
 * @param {T} defaultValue - Valor padrão caso a chave não exista
 * @returns {UseRemoteConfigReturn<T>} Objeto com value e loading
 *
 * @example
 * // Para boolean
 * const { value: showFlightDetails, loading } = useRemoteConfigFlag('show_flight_details', false);
 *
 * // Para string
 * const { value: theme } = useRemoteConfigFlag('theme', 'light');
 *
 * // Para number
 * const { value: maxItems } = useRemoteConfigFlag('max_items', 10);
 *
 * if (loading) return <div>Carregando...</div>;
 *
 * return showFlightDetails && <FlightDetails />;
 */
export const useRemoteConfigFlag = <T = any>(
  configKey: string,
  defaultValue: T
): UseRemoteConfigReturn<T> => {
  const value = useAppStore((state) => state.remoteConfigData?.[configKey]);
  const loading = value === undefined;

  useEffect(() => {
    getRemoteConfigData((err) => {
      if (err) {
        console.warn(`Erro ao carregar Remote Config (${configKey}):`, err);
      }
    });
  }, [configKey]);

  return {
    value: value ?? defaultValue,
    loading,
  };
};
